import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import jsPDF from "jspdf";
import CameraView from "./CameraView";
import ScannerControls from "./ScannerControls";
import CropAdjustView from "./CropAdjustView";
import PreviewView from "./PreviewView";
import ThumbnailStrip from "./ThumbnailStrip";
import ImagePreviewModal from "../ImagePreviewModal";
import {
  applyPerspectiveTransform,
  computeOutputDimensions,
} from "../../../utils/edgeDetection";
import { processImage } from "../../../utils/imageProcessing";
import { analyzeImageBlur } from "../../../utils/blurDetection";
import { swalError } from "../../../utils/swalAlert";
import "./DocScanner.css";

/**
 * DocScanner — Full Document Scanner Component
 *
 * Guaranteed hardware release:
 * - Tracks every allocated MediaStream and cancels in-flight getUserMedia promises.
 * - Stops all tracks synchronously on close, unmount, navigate, or upload.
 * - Persistent torch on rear cameras across multi-page scans.
 * - Robust 100% full-viewport coverage without flaky Fullscreen API.
 */
const DocScanner = ({
  onComplete,
  onClose,
  mode = "test",
  testId,
  studentId,
  studentName,
  captureType = "pdf",
  defaultScanMode = "color",
}) => {
  // ── Hardware & Stream Safety Tracking ─────────────────────────
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null);
  const activeStreamsRef = useRef(new Set()); // All MediaStreams ever opened
  const streamSeqRef = useRef(0); // Sequence token for in-flight cancellation
  const isMountedRef = useRef(true);
  const isFlashOnRef = useRef(false);
  const preferredTorchRef = useRef(false);
  const cameraViewRef = useRef(null);
  const shutterSoundRef = useRef(null);
  const activeBlobUrlsRef = useRef(new Set());

  // ── UI / Device State ─────────────────────────────────────────
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isFlashOn, setIsFlashOn] = useState(false);
  const [hasTorch, setHasTorch] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [currentCameraId, setCurrentCameraId] = useState(null);

  // ── Workflow Phase State ──────────────────────────────────────
  // 'camera' | 'crop' | 'preview'
  const [phase, setPhase] = useState("camera");
  const [autoCapture, setAutoCapture] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [scanMode, setScanMode] = useState(defaultScanMode); // 'color' (Enhanced) | 'bw' | 'original'

  // ── Current Capture In-Flight ─────────────────────────────────
  const [capturedCanvas, setCapturedCanvas] = useState(null);
  const [detectedCorners, setDetectedCorners] = useState(null);
  const [currentProcessedBlob, setCurrentProcessedBlob] = useState(null);
  const [currentPreviewUrl, setCurrentPreviewUrl] = useState(null);
  const [currentQuality, setCurrentQuality] = useState(null);
  const [currentDataUrl, setCurrentDataUrl] = useState(null);

  // ── Multi-Page List ───────────────────────────────────────────
  const [pages, setPages] = useState([]);
  const [showMultiPageModal, setShowMultiPageModal] = useState(false);
  const [retakeIndex, setRetakeIndex] = useState(null);

  // Keep ref in sync with state
  useEffect(() => {
    isFlashOnRef.current = isFlashOn;
  }, [isFlashOn]);

  // Check if camera stream is already running with live tracks
  const isStreamActive = useCallback(() => {
    return Boolean(
      streamRef.current &&
      streamRef.current.active &&
      streamRef.current.getVideoTracks().some((t) => t.readyState === "live")
    );
  }, []);

  // ── Safe Object URL Manager ───────────────────────────────────
  const createSafeUrl = useCallback((blob) => {
    const url = URL.createObjectURL(blob);
    activeBlobUrlsRef.current.add(url);
    return url;
  }, []);

  const revokeSafeUrl = useCallback((url) => {
    if (url && activeBlobUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      activeBlobUrlsRef.current.delete(url);
    }
  }, []);

  // ── Multi-Tier getUserMedia Fallback ──────────────────────────
  const getMediaStreamWithFallback = async (targetDeviceId = null) => {
    // Tier 1: Ideal 1080p, rear environment camera
    const tier1 = {
      video: targetDeviceId
        ? {
            deviceId: { exact: targetDeviceId },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          }
        : {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
    };

    try {
      return await navigator.mediaDevices.getUserMedia(tier1);
    } catch (err1) {
      console.warn("Tier 1 camera constraints failed, trying Tier 2:", err1);

      // Tier 2: Basic environment camera without resolution constraints
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
        });
      } catch (err2) {
        console.warn("Tier 2 camera constraints failed, trying Tier 3 (any camera):", err2);

        // Tier 3: Any video device (desktop webcam, budget phone, virtual camera)
        return await navigator.mediaDevices.getUserMedia({
          video: true,
        });
      }
    }
  };

  // ── HARDWARE CAMERA STOP (Guaranteed Zero-Leak) ───────────────
  const stopAllTracks = useCallback((resetFlash = false) => {
    // Invalidate any in-flight getUserMedia requests
    streamSeqRef.current++;

    // Turn off torch first if active
    if (streamRef.current) {
      try {
        const track = streamRef.current.getVideoTracks()[0];
        if (track && track.applyConstraints) {
          track.applyConstraints({ advanced: [{ torch: false }] }).catch(() => {});
        }
      } catch (e) {}
    }

    // 1. Stop current stream
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((track) => {
          try {
            track.enabled = false;
            track.stop();
          } catch (e) {}
        });
      } catch (e) {}
      streamRef.current = null;
    }

    // 2. Stop all streams in tracking set
    activeStreamsRef.current.forEach((s) => {
      try {
        s.getTracks().forEach((track) => {
          try {
            track.enabled = false;
            track.stop();
          } catch (e) {}
        });
      } catch (e) {}
    });
    activeStreamsRef.current.clear();

    // 3. Clear reactive stream state to immediately detach video.srcObject in CameraView
    setStream(null);
    setIsCameraReady(false);

    // Physically turn off flash state since stream stopped
    setIsFlashOn(false);
    isFlashOnRef.current = false;

    if (resetFlash) {
      preferredTorchRef.current = false;
      setHasTorch(false);
    }
  }, []);

  // ── CAMERA INITIALIZATION (With Cancellation & Torch Re-apply) ─
  const initializeCamera = useCallback(async (targetDeviceId = null) => {
    const seq = ++streamSeqRef.current;

    // Terminate existing stream before requesting new one
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach((t) => {
          try {
            t.enabled = false;
            t.stop();
          } catch (e) {}
        });
      } catch (e) {}
      streamRef.current = null;
    }
    setStream(null);
    setIsCameraReady(false);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera API not supported in this browser");
      }

      const newStream = await getMediaStreamWithFallback(targetDeviceId);

      // GUARD: If component unmounted or another camera request began while awaiting getUserMedia:
      if (!isMountedRef.current || seq !== streamSeqRef.current) {
        newStream.getTracks().forEach((t) => {
          try {
            t.enabled = false;
            t.stop();
          } catch (e) {}
        });
        return;
      }

      activeStreamsRef.current.add(newStream);
      streamRef.current = newStream;
      setStream(newStream); // REACTIVE UPDATE triggers CameraView effect to attach video.srcObject!

      const track = newStream.getVideoTracks()[0];
      if (track) {
        // Enumerate devices NOW that camera permission is granted (labels are populated)
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter((d) => d.kind === "videoinput");
          setCameras(videoDevices);

          const activeId = track.getSettings?.()?.deviceId;
          if (activeId) {
            setCurrentCameraId(activeId);
          }
        } catch (e) {
          console.warn("Device enumeration after grant error:", e);
        }

        // Torch detection & persistent application
        let canTorch = false;
        try {
          const capabilities = track.getCapabilities ? track.getCapabilities() : {};
          canTorch = Boolean(capabilities.torch);
        } catch (e) {}

        // Fallback: if not facing front/user, assume torch may be available
        if (!canTorch) {
          const settings = track.getSettings ? track.getSettings() : {};
          if (settings.facingMode !== "user") {
            canTorch = true;
          }
        }
        setHasTorch(canTorch);

        // If user had torch turned ON, re-apply it automatically!
        if (canTorch && preferredTorchRef.current) {
          try {
            await track.applyConstraints({
              advanced: [{ torch: true }],
            });
            setIsFlashOn(true);
            isFlashOnRef.current = true;
          } catch (torchErr) {
            console.warn("Could not re-apply torch on stream init:", torchErr);
          }
        }
      }

      setIsCameraReady(true);
    } catch (err) {
      if (seq === streamSeqRef.current && isMountedRef.current) {
        console.error("Camera init error:", err);
        swalError(`Camera error: ${err.message || "Failed to access camera"}`);
        setIsCameraReady(false);
      }
    }
  }, []);

  // ── Lifecycle & Mount/Unmount Cleanup ─────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    initializeCamera();

    const activeBlobUrls = activeBlobUrlsRef.current;

    const handleBeforeUnload = () => {
      stopAllTracks(true);
    };
    const handlePopState = () => {
      stopAllTracks(true);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);

      // Stop ALL camera hardware tracks immediately
      stopAllTracks(true);

      // Revoke all created blob URLs to free memory
      activeBlobUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {}
      });
      activeBlobUrls.clear();
    };
  }, [initializeCamera, stopAllTracks]);

  // Handle visibility / tab-switching to save battery
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        stopAllTracks(false); // Pause hardware, keep user flash preference
      } else if (phase === "camera") {
        if (!isStreamActive()) {
          initializeCamera();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [phase, initializeCamera, stopAllTracks, isStreamActive]);

  // ── Switch Camera ─────────────────────────────────────────────
  const handleCameraSwitch = () => {
    if (cameras.length <= 1) return;
    const currentIdx = cameras.findIndex((c) => c.deviceId === currentCameraId);
    const nextIdx = (currentIdx + 1) % cameras.length;
    const nextDeviceId = cameras[nextIdx].deviceId;
    setCurrentCameraId(nextDeviceId);
    initializeCamera(nextDeviceId);
  };

  // ── Toggle Flash ──────────────────────────────────────────────
  const handleFlashToggle = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    const nextState = !isFlashOn;
    try {
      await track.applyConstraints({
        advanced: [{ torch: nextState }],
      });
      setIsFlashOn(nextState);
      isFlashOnRef.current = nextState;
      preferredTorchRef.current = nextState;
      setHasTorch(true);
    } catch (err) {
      console.warn("Torch toggle failed:", err);
      // If error occurred when trying to turn ON, reset state
      if (nextState) {
        setIsFlashOn(false);
        isFlashOnRef.current = false;
        preferredTorchRef.current = false;
      }
    }
  };

  // ── Shutter Sound & Vibration ─────────────────────────────────
  const playShutter = useCallback(() => {
    if (navigator.vibrate) navigator.vibrate(60);
    try {
      if (shutterSoundRef.current) {
        shutterSoundRef.current.currentTime = 0;
        shutterSoundRef.current.play().catch(() => {});
      }
    } catch (e) {}
  }, []);

  // ── Step 1: Capture from Camera & Directly Open Preview ──────
  const handleFrameCapture = useCallback(
    async (canvas, corners) => {
      // 1. Play shutter sound and vibration immediately
      playShutter();

      // 2. Shut off camera hardware and torch immediately so camera is closed in preview
      if (streamRef.current) {
        try {
          const track = streamRef.current.getVideoTracks()[0];
          if (track && track.applyConstraints) {
            track.applyConstraints({ advanced: [{ torch: false }] }).catch(() => {});
          }
        } catch (e) {}
      }
      setIsFlashOn(false);
      isFlashOnRef.current = false;
      // Stop camera tracks without resetting preferredTorchRef
      stopAllTracks(false);

      setCapturedCanvas(canvas);

      // Validate corners or fallback to safe inset quadrilateral
      const validCorners =
        Array.isArray(corners) &&
        corners.length === 4 &&
        corners.every((c) => c && typeof c.x === "number" && !isNaN(c.x))
          ? corners
          : [
              { x: canvas.width * 0.05, y: canvas.height * 0.05 },
              { x: canvas.width * 0.95, y: canvas.height * 0.05 },
              { x: canvas.width * 0.95, y: canvas.height * 0.95 },
              { x: canvas.width * 0.05, y: canvas.height * 0.95 },
            ];

      setDetectedCorners(validCorners);

      try {
        // 3. Fast perspective transform on the captured canvas (~15ms)
        const dims = computeOutputDimensions(validCorners, 1600);
        const warped = applyPerspectiveTransform(
          canvas,
          validCorners,
          dims.width,
          dims.height
        );

        // 4. Synchronous instant DataURL: 100% reliable, zero delay
        const initialDataUrl = warped.toDataURL("image/jpeg", 0.85);
        setCurrentDataUrl(initialDataUrl);
        setCurrentPreviewUrl(initialDataUrl);

        // Also create initial blob from warped canvas
        try {
          warped.toBlob(
            (b) => {
              if (b) setCurrentProcessedBlob(b);
            },
            "image/jpeg",
            0.85
          );
        } catch (e) {}

        // 5. Instantly switch to preview phase! Zero waiting!
        setPhase("preview");
        setIsProcessing(true);

        // 6. Run enhanced scan filter asynchronously
        processImage(warped, scanMode)
          .then((processedBlob) => {
            const url = createSafeUrl(processedBlob);
            setCurrentProcessedBlob(processedBlob);
            setCurrentPreviewUrl(url);

            // Read processedBlob into currentDataUrl as well
            const reader = new FileReader();
            reader.onloadend = () => {
              if (reader.result) setCurrentDataUrl(reader.result);
            };
            reader.readAsDataURL(processedBlob);

            // Run blur analysis asynchronously
            analyzeImageBlur(url)
              .then((quality) => setCurrentQuality(quality))
              .catch((e) => console.warn("Blur analysis failed:", e));
          })
          .catch((err) => {
            console.error("Enhancement failed, using warped preview:", err);
          })
          .finally(() => {
            setIsProcessing(false);
          });
      } catch (err) {
        console.error("Auto-warp after capture failed, showing crop view:", err);
        setPhase("crop");
        setIsProcessing(false);
      }
    },
    [playShutter, scanMode, createSafeUrl, stopAllTracks]
  );

  // Manual capture button triggered from controls
  const handleManualCapture = () => {
    if (cameraViewRef.current) {
      cameraViewRef.current.capture();
    }
  };

  // ── Step 2: Apply Crop from CropAdjustView ────────────────────
  const handleApplyCrop = async (adjustedCorners) => {
    if (!capturedCanvas) return;
    setIsProcessing(true);
    try {
      setDetectedCorners(adjustedCorners);
      const dims = computeOutputDimensions(adjustedCorners, 1600);
      const warped = applyPerspectiveTransform(
        capturedCanvas,
        adjustedCorners,
        dims.width,
        dims.height
      );
      const initialDataUrl = warped.toDataURL("image/jpeg", 0.85);
      setCurrentDataUrl(initialDataUrl);

      const processedBlob = await processImage(warped, scanMode);
      const url = createSafeUrl(processedBlob);
      let quality = null;
      try {
        quality = await analyzeImageBlur(url);
      } catch (e) {
        console.warn("Blur analysis failed:", e);
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) setCurrentDataUrl(reader.result);
      };
      reader.readAsDataURL(processedBlob);

      setCurrentProcessedBlob(processedBlob);
      setCurrentPreviewUrl(url);
      setCurrentQuality(quality);
      setPhase("preview");
    } catch (err) {
      console.error("Failed to crop image:", err);
      swalError("Failed to apply crop");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelCrop = () => {
    // Return back to preview with current image intact
    setPhase("preview");
  };

  // ── Step 3: Change Scan Mode in Preview ───────────────────────
  const handleScanModeChange = async (newMode) => {
    if (newMode === scanMode || !capturedCanvas) return;
    setScanMode(newMode);
    setIsProcessing(true);
    try {
      const corners = detectedCorners || [
        { x: 0, y: 0 },
        { x: capturedCanvas.width - 1, y: 0 },
        { x: capturedCanvas.width - 1, y: capturedCanvas.height - 1 },
        { x: 0, y: capturedCanvas.height - 1 },
      ];
      const dims = computeOutputDimensions(corners, 1600);
      const warped = applyPerspectiveTransform(
        capturedCanvas,
        corners,
        dims.width,
        dims.height
      );
      const processedBlob = await processImage(warped, newMode);

      // Clean up previous preview URL
      revokeSafeUrl(currentPreviewUrl);
      const url = createSafeUrl(processedBlob);
      const quality = await analyzeImageBlur(url);

      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) setCurrentDataUrl(reader.result);
      };
      reader.readAsDataURL(processedBlob);

      setCurrentProcessedBlob(processedBlob);
      setCurrentPreviewUrl(url);
      setCurrentQuality(quality);
    } catch (err) {
      console.error("Filter change failed:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  // ── Step 4a: Accept Image into Multi-Page Document (Add Page) ─
  const handleAddPageFromPreview = () => {
    if (!currentProcessedBlob && !currentPreviewUrl && !currentDataUrl) return;

    const pageDataUrl =
      currentDataUrl ||
      (typeof currentPreviewUrl === "string" && currentPreviewUrl.startsWith("data:")
        ? currentPreviewUrl
        : null);

    const newPage = {
      id: Date.now(),
      blob: currentProcessedBlob,
      url: currentPreviewUrl,
      dataUrl: pageDataUrl,
      quality: currentQuality,
      mode: scanMode,
    };

    if (retakeIndex !== null) {
      setPages((prev) => {
        const oldUrl = prev[retakeIndex]?.url;
        revokeSafeUrl(oldUrl);
        return prev.map((p, idx) => (idx === retakeIndex ? newPage : p));
      });
      setRetakeIndex(null);
    } else {
      setPages((prev) => [...prev, newPage]);
    }

    // Reset current capture state
    setCapturedCanvas(null);
    setDetectedCorners(null);
    setCurrentProcessedBlob(null);
    setCurrentPreviewUrl(null);
    setCurrentDataUrl(null);
    setCurrentQuality(null);

    // Return to camera for next page
    setPhase("camera");
    if (!isStreamActive()) {
      initializeCamera(currentCameraId);
    }
  };

  // ── Step 4b: Save PDF immediately from Preview ────────────────
  const handleSavePDFFromPreview = async () => {
    if (!currentProcessedBlob && !currentPreviewUrl && !currentDataUrl) return;

    const pageDataUrl =
      currentDataUrl ||
      (typeof currentPreviewUrl === "string" && currentPreviewUrl.startsWith("data:")
        ? currentPreviewUrl
        : null);

    const newPage = {
      id: Date.now(),
      blob: currentProcessedBlob,
      url: currentPreviewUrl,
      dataUrl: pageDataUrl,
      quality: currentQuality,
      mode: scanMode,
    };

    let updatedPages;
    if (retakeIndex !== null) {
      updatedPages = pages.map((p, idx) => (idx === retakeIndex ? newPage : p));
      setRetakeIndex(null);
    } else {
      updatedPages = [...pages, newPage];
    }
    setPages(updatedPages);

    await handleGeneratePDF(updatedPages);
  };

  // ── Step 4c: Accept Single Image Mode ──────────────────────────
  const handleAcceptImage = async () => {
    if (!currentProcessedBlob || !currentPreviewUrl) return;

    setIsUploading(true);
    try {
      const file = new File([currentProcessedBlob], "captured_diagram.jpg", {
        type: "image/jpeg",
      });
      stopAllTracks(true);
      onComplete(file);
    } catch (err) {
      console.error("Failed to finish image capture:", err);
      swalError("Failed to process captured image");
    } finally {
      setIsUploading(false);
    }
  };

  // ── Step 5: Retake ────────────────────────────────────────────
  const handleRetake = () => {
    revokeSafeUrl(currentPreviewUrl);
    setCapturedCanvas(null);
    setDetectedCorners(null);
    setCurrentProcessedBlob(null);
    setCurrentPreviewUrl(null);
    setCurrentDataUrl(null);
    setCurrentQuality(null);
    setPhase("camera");
    if (!isStreamActive()) {
      initializeCamera(currentCameraId);
    }
  };

  // ── Multi-Page Review Modal Handlers ──────────────────────────
  const handleOpenMultiPageModal = () => {
    // 1. Turn off torch hardware immediately
    if (streamRef.current) {
      try {
        const track = streamRef.current.getVideoTracks()[0];
        if (track && track.applyConstraints) {
          track.applyConstraints({ advanced: [{ torch: false }] }).catch(() => {});
        }
      } catch (e) {}
    }
    setIsFlashOn(false);
    isFlashOnRef.current = false;

    // 2. Stop camera stream and release hardware completely (keep user's preferredTorchRef intact)
    stopAllTracks(false);

    // 3. Open modal
    setShowMultiPageModal(true);
  };

  const handleCloseMultiPageModal = () => {
    setShowMultiPageModal(false);
    // Restart camera, docscanner & torch on modal close
    if (phase === "camera") {
      initializeCamera(currentCameraId);
    }
  };

  const handleModalRetake = (index) => {
    setRetakeIndex(index);
    setShowMultiPageModal(false);
    setPhase("camera");
    initializeCamera(currentCameraId);
  };

  const handleModalDelete = (index) => {
    setPages((prev) => {
      const target = prev[index];
      if (target?.url) revokeSafeUrl(target.url);
      return prev.filter((_, idx) => idx !== index);
    });
  };

  // ── Thumbnail Single Page Delete ──────────────────────────────
  const handleDeletePage = (indexToDelete) => {
    setPages((prev) => {
      const pageToDelete = prev[indexToDelete];
      if (pageToDelete?.url) {
        revokeSafeUrl(pageToDelete.url);
      }
      return prev.filter((_, idx) => idx !== indexToDelete);
    });
  };

  // ── Step 6: Generate Final PDF & Complete ─────────────────────
  const handleGeneratePDF = async (customPages = null) => {
    // 1. Defensively validate customPages:
    // If an event object (e.g. from onClick) or anything other than an array is passed,
    // fall back cleanly to current pages state.
    const pagesToExport =
      Array.isArray(customPages) && customPages.length > 0
        ? customPages
        : Array.isArray(pages) && pages.length > 0
        ? pages
        : [];

    if (!pagesToExport || pagesToExport.length === 0) {
      swalError("No scanned pages to generate PDF");
      return;
    }

    setIsUploading(true);
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pagesToExport.length; i++) {
        if (i > 0) pdf.addPage("a4", "portrait");
        const page = pagesToExport[i];

        // 2. Guaranteed base64 DataURL resolution
        let dataUrl = page.dataUrl;

        if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
          if (page.url && typeof page.url === "string" && page.url.startsWith("data:")) {
            dataUrl = page.url;
          } else if (page.blob instanceof Blob) {
            dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                if (reader.result) resolve(reader.result);
                else reject(new Error("FileReader produced empty result"));
              };
              reader.onerror = reject;
              reader.readAsDataURL(page.blob);
            });
          } else if (page.url) {
            const res = await fetch(page.url);
            const blob = await res.blob();
            dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                if (reader.result) resolve(reader.result);
                else reject(new Error("FileReader produced empty result"));
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          }
        }

        if (!dataUrl || typeof dataUrl !== "string" || !dataUrl.startsWith("data:")) {
          throw new Error(`Failed to resolve image data for page ${i + 1}`);
        }

        // 3. Inspect image properties directly using jsPDF
        let imgWidth = 1200;
        let imgHeight = 1600;
        let fileType = "JPEG";

        try {
          const props = pdf.getImageProperties(dataUrl);
          if (props && props.width && props.height) {
            imgWidth = props.width;
            imgHeight = props.height;
            fileType = props.fileType || "JPEG";
          }
        } catch (propErr) {
          // Fallback via Image object
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              imgWidth = img.naturalWidth || img.width || 1200;
              imgHeight = img.naturalHeight || img.height || 1600;
              resolve();
            };
            img.onerror = reject;
            img.src = dataUrl;
          });
        }

        const imgAspect = imgWidth / imgHeight;
        const pageAspect = pageWidth / pageHeight;

        let renderW, renderH, offX, offY;
        if (imgAspect > pageAspect) {
          renderW = pageWidth;
          renderH = pageWidth / imgAspect;
          offX = 0;
          offY = (pageHeight - renderH) / 2;
        } else {
          renderH = pageHeight;
          renderW = pageHeight * imgAspect;
          offX = (pageWidth - renderW) / 2;
          offY = 0;
        }

        // Add to jsPDF using base64 dataUrl (instant, fully synchronous binary embedding)
        pdf.addImage(dataUrl, fileType, offX, offY, renderW, renderH, undefined, "FAST");
      }

      const pdfBlob = pdf.output("blob");
      console.log("Generated PDF blob size:", pdfBlob.size, "pages:", pagesToExport.length);

      if (!pdfBlob || pdfBlob.size < 2000) {
        throw new Error("Generated PDF is suspiciously empty or corrupt.");
      }

      const sanitizedName = studentName
        ? studentName.replace(/[^\x20-\x7E]/g, "").replace(/\s+/g, "_")
        : "";
      const safeName =
        sanitizedName.length > 0 && sanitizedName.replace(/_/g, "").length > 0
          ? sanitizedName
          : studentId || "student";

      const fileName =
        mode === "answer" && studentName
          ? `${safeName}_answer_paper.pdf`
          : mode === "bulk"
          ? `${safeName || "bulk_assessment"}_scan.pdf`
          : mode === "rubric"
          ? "captured_rubric.pdf"
          : "test_paper.pdf";

      const pdfFile = new File([pdfBlob], fileName, {
        type: "application/pdf",
      });

      // Synchronously stop all hardware camera tracks
      stopAllTracks(true);

      onComplete(pdfFile);
    } catch (err) {
      console.error("PDF generation failed:", err);
      swalError("Failed to generate PDF. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    stopAllTracks(true);
    if (onClose) onClose();
  };

  return (
    <div className="ds-container">
      {/* Top Header */}
      <div className="ds-header">
        <div className="ds-header-left">
          <img src="/imgs/logo.svg" alt="logo" />
          <span className="ds-mode-badge">{mode}</span>
        </div>
        <div className="ds-header-right">
          {studentName && (
            <div className="ds-student-badge" title={studentName}>
              {studentName}
            </div>
          )}
          {phase === "preview" && captureType === "pdf" && (
            <button
              type="button"
              className="ds-header-save-btn"
              onClick={handleSavePDFFromPreview}
              disabled={isProcessing || !currentPreviewUrl}
              title="Save all pages as PDF"
            >
              ✓ Save PDF {pages.length > 0 ? `(${pages.length + 1})` : ""}
            </button>
          )}
          <button
            className="ds-close-btn"
            onClick={handleClose}
            aria-label="Close Scanner"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Main Viewport: Camera is active only during phase === "camera" and when multi-page modal is NOT open */}
      {phase === "camera" && !showMultiPageModal && (
        <div className="ds-camera-wrapper">
          <CameraView
            ref={cameraViewRef}
            stream={stream}
            onCapture={handleFrameCapture}
            autoCapture={autoCapture}
            isProcessing={isProcessing}
            phase={phase}
          />

          {/* Thumbnail Strip if pages already captured */}
          {pages.length > 0 && (
            <ThumbnailStrip
              pages={pages}
              onPageTap={handleOpenMultiPageModal}
              onDeletePage={handleDeletePage}
              onDone={() => handleGeneratePDF()}
            />
          )}

          {/* Bottom Scanner Controls */}
          <ScannerControls
            onCapture={handleManualCapture}
            onFlashToggle={handleFlashToggle}
            onCameraSwitch={handleCameraSwitch}
            isFlashOn={isFlashOn}
            autoCapture={autoCapture}
            onAutoCaptureToggle={() => setAutoCapture(!autoCapture)}
            pageCount={pages.length}
            isCameraReady={isCameraReady}
            isProcessing={isProcessing}
            stability={cameraViewRef.current?.getStability?.() || 0}
            hasTorch={hasTorch}
            cameraCount={cameras.length}
          />
        </div>
      )}

      {phase === "crop" && capturedCanvas && (
        <CropAdjustView
          canvas={capturedCanvas}
          corners={detectedCorners}
          onApply={handleApplyCrop}
          onCancel={handleCancelCrop}
        />
      )}

      {phase === "preview" && (
        <PreviewView
          blobUrl={currentPreviewUrl}
          onAccept={handleAcceptImage}
          onAddPage={handleAddPageFromPreview}
          onRetake={handleRetake}
          onAdjustCrop={() => setPhase("crop")}
          scanMode={scanMode}
          onScanModeChange={handleScanModeChange}
          quality={currentQuality}
          isProcessing={isProcessing}
          captureType={captureType}
        />
      )}

      {/* Multi-page review modal */}
      {showMultiPageModal && (
        <ImagePreviewModal
          images={pages.map((p) => p.url)}
          imageQualities={pages.map((p) => p.quality)}
          onClose={handleCloseMultiPageModal}
          onRetake={handleModalRetake}
          onDelete={handleModalDelete}
          onUpload={() => handleGeneratePDF()}
          isUploading={isUploading}
        />
      )}

      <audio ref={shutterSoundRef} src="/sounds/shutter.mp3" preload="auto" />
    </div>
  );
};

export default DocScanner;
